import {
  People as PeopleIcon,
  Payments as PaymentsIcon,
  CurrencyExchange as CurrencyExchangeIcon,
  Autorenew as AutorenewIcon,
  Shop2 as ShopsIcon,
  Timeline as TimelineIcon,
  Assignment as AssignmentIcon,
  Analytics as AnalyticsIcon,
  Groups as GroupsIcon,
  Wallet as WalletIcon,
  CreditCard as CreditCardIcon,
  BarChart as BarChartIcon,
  AccountBalance as AccountBalanceIcon,
  MonetizationOnOutlined as MonetizationOnOutlinedIcon,
  Report as ReportIcon,
  AutoMode as AutoModeIcon,
  Abc as AbcIcon,
  MenuBook as MenuBookIcon,
  Archive as ArchiveIcon,
  Webhook as WebhookIcon,
  QueryStats as QueryStatsIcon,
  CompareArrows as CompareArrowsIcon,
  Receipt as ReceiptIcon,
  RequestQuote as RequestQuoteIcon,
  Balance as BalanceIcon,
  Route as RouteIcon,
  Person as PersonIcon,
  Settings as SettingsIcon,
  TimerOutlined as TimerOutlinedIcon,
  Telegram as TelegramIcon,
  AccountTree as AccountTreeIcon,
} from '@mui/icons-material';
import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { LayoutMenuSection } from 'components';
import { ROUTE_PATH } from 'constants/routes';
import { TranslationNamespace } from 'i18n';
import { UserLayout } from 'layouts/User';

type Props = {
  children: React.ReactElement;
};

export const AdminLayout: React.FC<Props> = ({ children }: Props) => {
  const { t } = useTranslation(TranslationNamespace.Admin, {
    keyPrefix: 'layout',
  });
  const menuSections: LayoutMenuSection[] = useMemo(
    () => [
      {
        title: t('menu_sections.cash_flow'),
        items: [
          {
            title: t('menu_items.assets'),
            MuiIcon: AnalyticsIcon,
            link: ROUTE_PATH.ADMIN.MAIN,
          },
          {
            title: t('menu_items.funds_requests'),
            MuiIcon: PaymentsIcon,
            link: ROUTE_PATH.ADMIN.FUNDS_REQUESTS,
          },
          {
            title: t('menu_items.internal_transfers'),
            MuiIcon: CompareArrowsIcon,
            link: ROUTE_PATH.ADMIN.INTERNAL_TRANSFERS,
          },
          {
            title: t('menu_items.orders'),
            MuiIcon: AssignmentIcon,
            link: ROUTE_PATH.ADMIN.PAYIN_ORDERS,
          },
          {
            title: t('menu_items.currency_exchange'),
            MuiIcon: CurrencyExchangeIcon,
            link: ROUTE_PATH.ADMIN.CURRENCY_EXCHANGE,
          },
        ],
      },
      {
        title: t('menu_sections.commissions'),
        items: [
          {
            title: t('menu_items.traders'),
            MuiIcon: BarChartIcon,
            link: ROUTE_PATH.ADMIN.TRADERS,
          },

          {
            title: t('menu_items.shops'),
            MuiIcon: ShopsIcon,
            link: ROUTE_PATH.ADMIN.SHOPS,
          },
          {
            title: t('menu_items.cashback'),
            MuiIcon: RequestQuoteIcon,
            link: ROUTE_PATH.ADMIN.CASHBACK,
          },
        ],
      },
      {
        title: t('menu_sections.platform'),
        items: [
          {
            title: t('menu_items.internal_wallets'),
            MuiIcon: WalletIcon,
            link: ROUTE_PATH.ADMIN.WALLETS,
          },
          {
            title: t('menu_items.transactions'),
            MuiIcon: ReceiptIcon,
            link: ROUTE_PATH.ADMIN.TRANSACTIONS,
          },
          {
            title: t('menu_items.groups'),
            MuiIcon: GroupsIcon,
            link: ROUTE_PATH.ADMIN.GROUPS,
          },
          {
            title: t('menu_items.users'),
            MuiIcon: PeopleIcon,
            link: ROUTE_PATH.ADMIN.USERS,
          },
          {
            title: t('menu_items.currencies'),
            MuiIcon: MonetizationOnOutlinedIcon,
            link: ROUTE_PATH.ADMIN.CURRENCIES,
          },
          {
            title: t('menu_items.banks'),
            MuiIcon: AccountBalanceIcon,
            link: ROUTE_PATH.ADMIN.BANKS,
          },
          {
            title: t('menu_items.trade_methods'),
            MuiIcon: AutorenewIcon,
            link: ROUTE_PATH.ADMIN.TRADE_METHODS,
          },
          {
            title: t('menu_items.order_automation'),
            MuiIcon: AutoModeIcon,
            link: ROUTE_PATH.ADMIN.ORDER_AUTOMATION,
          },
          {
            title: t('menu_items.automation_sources'),
            MuiIcon: AbcIcon,
            link: ROUTE_PATH.ADMIN.AUTOMATION_SOURCES,
          },
          {
            title: t('menu_items.archive'),
            MuiIcon: ArchiveIcon,
            link: ROUTE_PATH.ADMIN.ARCHIVE,
          },
          {
            title: t('menu_items.webhooks'),
            MuiIcon: WebhookIcon,
            link: ROUTE_PATH.ADMIN.WEBHOOKS,
          },
          {
            title: t('menu_items.sci_domains'),
            MuiIcon: WebhookIcon,
            link: ROUTE_PATH.ADMIN.SCI_DOMAINS,
          },
          {
            title: t('menu_items.p2p_providers'),
            MuiIcon: RouteIcon,
            link: ROUTE_PATH.ADMIN.P2P_PROVIDERS,
          },
          {
            title: t('menu_items.p2p_provider_webhooks'),
            MuiIcon: WebhookIcon,
            link: ROUTE_PATH.ADMIN.P2P_PROVIDER_WEBHOOKS,
          },
          {
            title: t('menu_items.p2p_provider_payin_customer_daily_limits'),
            MuiIcon: RouteIcon,
            link: ROUTE_PATH.ADMIN.P2P_PROVIDER_PAYIN_CUSTOMER_DAILY_LIMITS,
          },
          {
            title: t('menu_items.cascades'),
            MuiIcon: AccountTreeIcon,
            link: ROUTE_PATH.ADMIN.CASCADES,
          },
          {
            title: t('menu_items.customers'),
            MuiIcon: PersonIcon,
            link: ROUTE_PATH.ADMIN.CUSTOMERS,
          },
          {
            title: t('menu_items.configs'),
            MuiIcon: SettingsIcon,
            link: ROUTE_PATH.ADMIN.CONFIGS,
          },
          {
            title: t('menu_items.telegram_flow'),
            MuiIcon: TelegramIcon,
            link: ROUTE_PATH.ADMIN.TELEGRAM_FLOW,
          },
        ],
      },
      {
        title: t('menu_sections.analytics'),
        items: [
          {
            title: t('menu_items.requisites'),
            MuiIcon: CreditCardIcon,
            link: ROUTE_PATH.ADMIN.REQUISITES,
          },
          {
            title: t('menu_items.asset_balance_history'),
            MuiIcon: TimelineIcon,
            link: ROUTE_PATH.ADMIN.ASSET_BALANCE_HISTORY,
          },
          {
            title: t('menu_items.anomalies'),
            MuiIcon: ReportIcon,
            link: ROUTE_PATH.ADMIN.ANOMALIES,
          },
          {
            title: t('menu_items.statistics'),
            MuiIcon: QueryStatsIcon,
            link: ROUTE_PATH.ADMIN.STATISTICS,
          },
          {
            title: t('menu_items.balance_reconciliation'),
            MuiIcon: BalanceIcon,
            link: ROUTE_PATH.ADMIN.BALANCE_RECONCILIATION,
          },
          {
            title: t('menu_items.audit'),
            MuiIcon: MenuBookIcon,
            link: ROUTE_PATH.ADMIN.AUDIT,
          },
          {
            title: t('menu_items.execution_metrics'),
            MuiIcon: TimerOutlinedIcon,
            link: ROUTE_PATH.ADMIN.EXECUTION_METRICS,
          },
        ],
      },
    ],
    [t],
  );
  return <UserLayout menuSections={menuSections}>{children}</UserLayout>;
};
